/**
 * @file Entrypoint embeddabile del widget di chat (fase 1 ottimizzazione: lazy loading totale).
 * @description Carica il codice React e dell'assistente solo al primo click dell'utente (o se `open: true`).
 * Riduce drasticamente il payload iniziale: questo file resta minimale, il resto va in chunk separati.
 * Esempio:
 * <script>
 *   window.CHAT_WIDGET_CONFIG = { apiUrl: 'https://api.example.com/assistant', position: 'bottom-right', open: false };
 * </script>
 * <script type="module" src="/path/to/chat-widget.js"></script>
 * Nota: React e ReactDOM sono esterni: l'host deve già includere (ESM) react & react-dom.
 */

import type { WidgetConfig, ChatWidgetStrings } from "chat-core";
import { dispatchPrompt, SentryWrapper } from "chat-core";
import type { WidgetData } from "./types";
import { adaptCssForShadowDOM } from "./shadow-dom-css-adapter";

declare global {
  interface Window {
    CHAT_WIDGET_CONFIG?: WidgetConfig;
    NextbotChatWidgetConfig?: WidgetConfig;
    __NEXTBOT_WIDGET_CONFIG__?: WidgetConfig;
    CHAT_WIDGET_DATA?: WidgetData;
    CHAT_WIDGET_STRINGS?: ChatWidgetStrings;
    __NEXTBOT_CHAT_WIDGET_INTERNAL__?: { initialized: boolean };
    // Namespace esposta dal widget per API globali sicure.
    NextBot?: {
      dispatchPrompt?: (prompt: string) => void;
      // estendibile in futuro con altre API globali.
    };
    __NEXTBOT_SENTRY_TEST__?: {
      throwAsync: () => void;
      rejectPromise: () => void;
      captureAndRethrow: () => void;
    };
  }
}

/**
 * Flag di ambiente di sviluppo.
 *
 * Utilizzato per abilitare funzionalità specifiche in fase di sviluppo,
 * come il caricamento diretto del CSS per supportare HMR con Vite.
 * Questo flag è `true` solo durante lo sviluppo con Vite (demo page).
 */
const IS_DEV: boolean =
  typeof import.meta !== "undefined" && (import.meta as unknown as { env?: { DEV?: boolean } }).env?.DEV === true;

if (!window.__NEXTBOT_CHAT_WIDGET_INTERNAL__) window.__NEXTBOT_CHAT_WIDGET_INTERNAL__ = { initialized: false };
const internalState = window.__NEXTBOT_CHAT_WIDGET_INTERNAL__!;
// Espongo delle funzioni globali per interagire con il Chatbot.
if (!window.NextBot) window.NextBot = {};
window.NextBot.dispatchPrompt = dispatchPrompt;
if (!internalState.initialized) {
  internalState.initialized = true;
  init();
}

/**
 * Legge la configurazione del widget da variabili globali.
 *
 * Cerca la configurazione in ordine di priorità:
 * 1. `window.CHAT_WIDGET_CONFIG`
 * 2. `window.NextbotChatWidgetConfig`
 * 3. `window.__NEXTBOT_WIDGET_CONFIG__`
 *
 * @returns Configurazione del widget o `null` se `apiUrl` mancante
 */
function readGlobalConfig(): WidgetConfig | null {
  const cfg: WidgetConfig | undefined =
    window.CHAT_WIDGET_CONFIG || window.NextbotChatWidgetConfig || window.__NEXTBOT_WIDGET_CONFIG__;
  if (!cfg || !cfg.apiUrl) return null;
  return {
    apiUrl: cfg.apiUrl,
    position: cfg.position || "bottom-right",
    open: !!cfg.open,
    mode: cfg.mode || "input",
    customCss: cfg.customCss || undefined,
    auth: cfg.auth === true,
    getAuthToken: typeof cfg.getAuthToken === "function" ? cfg.getAuthToken : undefined,
    brand: cfg.brand || "nextwork360",
    container: cfg.container ?? null,
    useHistory: cfg.useHistory === true,
    useLocalThreadList: cfg.useLocalThreadList === true,
    enableAiTitleGeneration: cfg.enableAiTitleGeneration === true,
    standaloneAppUrl: cfg.standaloneAppUrl,
    serviceApi: cfg.serviceApi ?? "",
    debugMode: cfg.debugMode ?? false,
    sentryEnabled: cfg.sentryEnabled === true,
    themeVars: cfg.themeVars || undefined,
    imageStorageUrl: cfg.imageStorageUrl || undefined,
    stt: cfg.stt,
  };
}

/**
 * Legge dati runtime opzionali forniti dall'host via variabile globale.
 *
 * Sanitizza e valida i dati prima di restituirli:
 * - `siteName`: Stringa non vuota
 * - `postId`: Numero finito
 * - `postTitle`: Stringa non vuota
 *
 * @returns Dati runtime validati o `null` se non disponibili
 */
function readGlobalData(): WidgetData | null {
  const raw = window.CHAT_WIDGET_DATA;
  if (!raw) return null;

  const data: WidgetData = {};

  if (typeof raw.siteName === "string") {
    const sanitizedName = raw.siteName.trim();
    if (sanitizedName) data.siteName = sanitizedName;
  }

  if (typeof raw.postId === "number" && Number.isFinite(raw.postId)) {
    data.postId = raw.postId;
  }

  if (typeof raw.postTitle === "string") {
    const sanitizedTitle = raw.postTitle.trim();
    if (sanitizedTitle) data.postTitle = sanitizedTitle;
  }

  return data;
}

/**
 * Risolve un riferimento container in un elemento HTML.
 *
 * Accetta:
 * - Stringa selettore CSS
 * - `null` / `undefined`
 *
 * @param c - Selettore CSS o null
 * @returns HTMLElement risolto o `null`
 */
function resolveContainer(c?: string | null): HTMLElement | null {
  if (!c) return null;
  try {
    const found = document.querySelector(c);
    return found instanceof HTMLElement ? found : null;
  } catch {
    return null;
  }
}

/**
 * Inizializza il widget di chat.
 *
 * Workflow:
 * 1. Legge configurazione e dati da variabili globali
 * 2. Crea container di montaggio con posizionamento configurato
 * 3. Carica e monta il widget dopo `window.load` (o subito se già caricato)
 *
 * @remarks
 * Il widget viene caricato automaticamente dopo il completo caricamento della pagina
 * per garantire che tutte le risorse (immagini, font, ecc.) siano pronte.
 */
function init(): void {
  const config = readGlobalConfig();
  if (!config) {
    console.warn("chat-widget: apiUrl mancante in window.CHAT_WIDGET_CONFIG");
    return;
  }

  const data = readGlobalData();

  // Inizializza Sentry solo se il DSN è nel bundle (build-time) E l'host
  // l'ha abilitata via config runtime (`sentryEnabled`, default false).
  try {
    const sentryDsn = import.meta.env.VITE_WIDGET_SENTRY_DSN;
    if (sentryDsn && config.sentryEnabled) {
      const sentrySampleRateEnv = import.meta.env.VITE_WIDGET_SENTRY_SAMPLE_RATE;
      const parsedSampleRate = sentrySampleRateEnv === undefined ? NaN : Number(sentrySampleRateEnv);
      const sentrySampleRate = Number.isNaN(parsedSampleRate) ? 0.1 : parsedSampleRate;

      SentryWrapper.initSentry({
        dsn: sentryDsn,
        environment: import.meta.env.VITE_WIDGET_SENTRY_ENV,
        release: import.meta.env.VITE_CHAT_WIDGET_VERSION,
        sampleRate: sentrySampleRate,
        config,
        data: data ?? undefined,
        // URL reale del bundle come caricato dal sito host: abilita i listener
        // globali filtrati (vedi chat-core/telemetry/global-error-listeners.ts)
        bundleUrl: import.meta.url,
      });
    }
  } catch (e) {
    console.warn("Failed to init Sentry", e);
  }

  const xContainerEl = resolveContainer(config.container?.xAxis);
  const yContainerEl = resolveContainer(config.container?.yAxis);
  const mount = createMountContainer(config.position || "bottom-right", 0, xContainerEl, yContainerEl);

  // Flag per evitare doppi caricamenti.
  let loaded = false;
  const load = async () => {
    if (loaded) return;
    loaded = true;
    await renderWidget(mount, config, data ?? undefined);
  };

  const schedule = () => {
    if (document.readyState === "complete") {
      // La pagina è già completamente caricata.
      queueMicrotask(load);
    } else {
      // Attendi l'evento load per essere sicuri che tutte le risorse (immagini, ecc.) siano pronte.
      window.addEventListener("load", () => load(), { once: true });
    }
  };
  schedule();

  // Hook di verifica manuale dei listener globali Sentry (solo sviluppo).
  // Definiti QUI perché il loro stack deve puntare al bundle del widget.
  if (IS_DEV) {
    window.__NEXTBOT_SENTRY_TEST__ = {
      throwAsync: () => {
        setTimeout(() => {
          throw new Error("[test] errore widget async non gestito");
        }, 0);
      },
      rejectPromise: () => {
        void Promise.reject(new Error("[test] rejection widget non gestita"));
      },
      captureAndRethrow: () => {
        setTimeout(() => {
          try {
            throw new Error("[test] errore catturato e rilanciato");
          } catch (e) {
            SentryWrapper.captureException(e);
            throw e; // il listener globale deve saltarlo (WeakSet) → 1 solo evento
          }
        }, 0);
      },
    };
  }
}

/**
 * Inietta un singolo foglio di stile CSS nel target specificato.
 *
 * Supporta due modalità:
 * - **Shadow DOM**: Fetcha il CSS e lo inietta come `<style>` inline (con adattamento)
 * - **Document**: Crea un `<link>` element standard
 *
 * @param url - URL del foglio di stile
 * @param id - ID univoco per l'elemento style/link
 * @param target - Destinazione (ShadowRoot o document.head)
 * @returns Promise che si risolve quando il CSS è caricato
 */
async function injectStylesheet(url: string, id: string, target: ShadowRoot | HTMLHeadElement): Promise<void> {
  // Evita duplicati.
  if (target.querySelector(`#${id}`)) return;

  // Shadow DOM: fetch + <style> inline.
  if (target instanceof ShadowRoot) {
    const response = await fetch(url);
    const css = await response.text();
    const shadowCompatibleCss = adaptCssForShadowDOM(css);
    const style = document.createElement("style");
    style.id = id;
    style.textContent = shadowCompatibleCss;
    target.appendChild(style);
    return;
  }

  // Document: <link> standard.
  return new Promise((resolve, reject) => {
    const link = document.createElement("link");
    link.id = id;
    link.rel = "stylesheet";
    link.href = url;
    link.onload = () => resolve();
    link.onerror = () => reject(new Error(`Errore caricamento CSS: ${url}`));
    target.appendChild(link);
  });
}

/**
 * Inietta CSS nel widget (base + custom).
 *
 * Gestisce:
 * - CSS base del widget (`chat-ui.css`)
 * - CSS custom forniti dall'host
 * - Adattamento automatico per Shadow DOM
 *
 * @param baseFilename - Nome file CSS base (relativo allo script del widget)
 * @param customUrls - URL di fogli di stile custom (opzionale)
 * @param target - Destinazione (ShadowRoot o document.head)
 *
 * @remarks
 * Gli errori di caricamento vengono loggati ma non bloccano l'esecuzione
 * per garantire la resilienza del widget anche se alcuni CSS non sono disponibili.
 */
async function injectCss(
  baseFilename: string,
  customUrls: string | string[] | undefined,
  target: ShadowRoot | HTMLHeadElement,
): Promise<void> {
  // Risolvi URL base relativo allo script del widget.
  let baseUrl = baseFilename;
  try {
    const scriptEl = document.currentScript as HTMLScriptElement | null;
    const scriptUrl =
      scriptEl?.src ?? (typeof import.meta !== "undefined" ? (import.meta as { url?: string }).url : undefined);
    if (scriptUrl) {
      baseUrl = new URL(baseFilename, new URL(".", scriptUrl).href).href;
    }
  } catch {
    // Usa il filename così com'è se non riesce a risolvere.
  }

  // Inietta CSS base.
  try {
    if (IS_DEV) {
      try {
        // Questo permette a Vite di collegare il CSS al grafo dei moduli e abilitare HMR.
        await import("chat-ui/chat-ui.css");
        console.log("chat-widget: CSS base importato via modulo (dev mode)");
      } catch {
        console.warn("chat-widget: errore caricamento CSS base via modulo (dev mode)");
      }
    } else {
      await injectStylesheet(baseUrl, "chat-widget-base-css", target);
    }
  } catch (err) {
    console.warn("chat-widget: errore caricamento CSS base", baseUrl, err);
  }

  // Inietta CSS custom (se forniti).
  const urls = customUrls ? (Array.isArray(customUrls) ? customUrls : [customUrls]) : [];
  await Promise.allSettled(
    urls
      .filter((url) => url && typeof url === "string")
      .map(async (url) => {
        const urlHash = btoa(url)
          .replace(/[^a-z0-9]/gi, "")
          .slice(0, 12);
        const customId = `chat-widget-styles-custom-${urlHash}`;
        return injectStylesheet(url, customId, target).catch((err) => {
          console.warn("chat-widget: errore caricamento CSS custom", url, err);
        });
      }),
  );
}

/**
 * Applica le variabili CSS di theming custom al widget.
 *
 * Supporta due modalità:
 * - **Shadow DOM**: Inietta `<style>` con selettore `:host`
 * - **Document**: Inietta `<style>` con selettore `:root` nel document.head
 *
 * @param themeVars - Mappa di variabili CSS personalizzate (es. `{ "--nextbot-chat-accent": "#FF5722" }`)
 * @param target - Shadow root o null per mount diretto
 *
 * @remarks
 * Nel caso di mount diretto, le variabili vengono applicate globalmente a `:root`
 * per garantire che siano accessibili a tutto il DOM del widget.
 */
function applyThemeVars(themeVars: Record<string, string>, target: ShadowRoot | null): void {
  const themeStyle = document.createElement("style");
  themeStyle.id = "chat-widget-theme-override";
  const cssVars = Object.entries(themeVars)
    .map(([key, value]) => `${key}: ${value}`)
    .join("; ");

  // Shadow DOM: usa :host per scoping nello shadow root.
  if (target instanceof ShadowRoot) {
    themeStyle.textContent = `:host { ${cssVars}; }`;
    target.appendChild(themeStyle);
    return;
  }

  // Mount diretto: usa :root e appendi al document.head.
  themeStyle.textContent = `:root { ${cssVars}; }`;
  document.head.appendChild(themeStyle);
}

/**
 * Prepara la struttura DOM per il widget nello Shadow DOM.
 *
 * Crea:
 * - Container per portali React (overlay, tooltip, dialog)
 * - Mount point per l'app React principale
 *
 * @param shadowRoot - Shadow root in cui creare la struttura
 * @returns Oggetti DOM creati (portalContainer, reactMount)
 */
function prepareShadowDOMStructure(shadowRoot: ShadowRoot): {
  portalContainer: HTMLDivElement;
  reactMount: HTMLDivElement;
} {
  const portalContainer = document.createElement("div");
  portalContainer.id = "widget-portal-root";
  portalContainer.style.position = "relative";
  portalContainer.style.zIndex = "9999";
  shadowRoot.appendChild(portalContainer);

  const reactMount = document.createElement("div");
  reactMount.id = "widget-react-root";
  shadowRoot.appendChild(reactMount);

  return { portalContainer, reactMount };
}

/**
 * Carica e monta l'app React del widget.
 *
 * Importa dinamicamente React, ReactDOM e il componente principale,
 * poi esegue il render nell'elemento specificato.
 * Integra i callback Sentry per l'error handling di React.
 *
 * @param reactMount - Elemento DOM in cui montare React
 * @param config - Configurazione del widget
 * @param data - Dati runtime opzionali
 * @param shadowRoot - Shadow root (null se mount diretto)
 * @param portalContainer - Container per portali (null se mount diretto)
 */
async function mountReactApp(
  reactMount: HTMLElement,
  config: WidgetConfig,
  data: WidgetData | undefined,
  shadowRoot: ShadowRoot | null,
  portalContainer: HTMLDivElement | null,
): Promise<void> {
  const React = await import("react");
  const ReactDOMClient = await import("react-dom/client");
  const { WidgetRoot } = await import("./widget-root");
  const { getReactErrorHandlers } = await import("chat-core");

  // Ottieni i callback Sentry per l'error handling di React
  const errorHandlers = getReactErrorHandlers();

  // Crea root React con error handling integrato
  const root = ReactDOMClient.createRoot(reactMount, errorHandlers);
  root.render(React.createElement(WidgetRoot, { config, data, shadowRoot, portalContainer }));
}

/**
 * Crea o recupera il contenitore per il montaggio del widget.
 *
 * Gestisce:
 * - Posizionamento fisso o assoluto (basato su `config.mode`)
 * - Calcolo dinamico di dimensioni massime e offset
 * - Observer per ridimensionamento viewport e header sticky
 *
 * @param position - Posizione del widget (es. "bottom-right", "top-left")
 * @param gap - Distanza dai bordi in pixel
 * @param xContainer - Elemento container host per posizionamento orizzontale (opzionale)
 * @param yContainer - Elemento container host per posizionamento verticale (opzionale)
 * @returns Elemento container creato o esistente
 */
function createMountContainer(
  position: string,
  gap: number,
  xContainer?: HTMLElement | null,
  yContainer?: HTMLElement | null,
): HTMLElement {
  const existing = document.getElementById("chat-widget-root");
  if (existing) return existing;

  const config = readGlobalConfig();

  const el = document.createElement("div");
  el.id = "chat-widget-root";
  el.style.position = config?.mode === "input" ? "fixed" : "absolute";
  el.style.width = "100%";
  el.style.zIndex = config?.mode === "input" ? "5000" : "";

  const gapPx = `${gap}px`;
  if (position.includes("bottom")) el.style.bottom = `calc(env(safe-area-inset-bottom, 0px) + ${gapPx})`;
  else el.style.top = gapPx;

  /**
   * Ottiene le dimensioni correnti della viewport, considerando il visual viewport se disponibile.
   *
   * @returns Oggetto con larghezza (`vw`) e altezza (`vh`) della viewport
   */
  const getViewportSize = (): { vw: number; vh: number } => {
    // Usa visualViewport se disponibile (più preciso su mobile con tastiera aperta).
    if (window.visualViewport) {
      return {
        vw: window.visualViewport.width,
        vh: window.visualViewport.height,
      };
    }

    // Fallback: window.innerWidth/Height con ulteriore fallback su documentElement.
    return {
      vw: window.innerWidth ?? document.documentElement.clientWidth,
      vh: window.innerHeight ?? document.documentElement.clientHeight,
    };
  };

  const MIN_TOP_GAP_PX = 16;
  const EPSILON = 1; // Tolleranza per differenze sub-pixel nel check sticky.

  /**
   * Applica i vincoli di posizionamento e dimensionamento al contenitore del widget.
   *
   * @returns void
   */
  const applyConstraints = () => {
    const xRect = xContainer?.getBoundingClientRect();
    const yRect = yContainer?.getBoundingClientRect() || xRect; // fallback a xContainer se yContainer non fornito

    if (!xRect) {
      if (position.includes("right")) {
        el.style.right = gapPx;
        el.style.left = "";
      } else {
        el.style.left = gapPx;
        el.style.right = "";
      }
      return;
    }

    const { vw, vh } = getViewportSize();
    const leftOffset = Math.max(xRect.left, 0) + gap;
    const rightOffset = Math.max(vw - xRect.right, 0) + gap;
    const maxWidth = Math.max(xRect.width - gap * 2, 0);

    // Calcola la posizione del yContainer relativa all'inizio del body (scroll-invariant
    // per elementi nel normale flow del documento, dove yContainer scorre insieme al body).
    // Questo rende il calcolo immune al margin-top del body (es. classe .skin) e agli
    // elementi non-fixed soprastanti (es. banner pubblicitari), poiché sia yRect.top
    // che bodyRect.top si spostano della stessa quantità al variare dello scroll in
    // presenza di un unico contesto di scroll (non valido per elementi fixed o contenitori
    // con scroll indipendente).
    const bodyRect = document.body.getBoundingClientRect();
    const yOffsetWithinBody = Math.max(yRect.top - bodyRect.top, 0);
    const adminBarHeight =
      parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--wp-admin--admin-bar--height")) || 0;

    // Calcola il bottom più basso tra gli elementi fixed o sticky (attivamente bloccati in cima)
    // presenti nella pagina. Usare il massimo di bottom (invece di sommare le altezze) gestisce
    // correttamente sia header sovrapposti/side-by-side (che verrebbero contati doppio con la somma)
    // sia header parzialmente nascosti sopra il viewport (bottom rimane corretto).
    // Nota: gli header sticky vengono contati solo quando effettivamente bloccati (rect.top <= stickyTop).
    // Il calcolo non si aggiorna durante lo scroll (solo su resize/MutationObserver): comportamento accettato.
    const pinnedHeadersMaxBottom = Array.from(document.querySelectorAll<HTMLElement>("header > nav, .header-d360"))
      .reduce((max, headerEl) => {
        if (headerEl.offsetHeight <= 0) return max;
        const computedStyle = getComputedStyle(headerEl);
        const cssPosition = computedStyle.position;
        const rect = headerEl.getBoundingClientRect();
        if (cssPosition === "fixed") return Math.max(max, rect.bottom);
        if (cssPosition === "sticky") {
          const topValue = parseFloat(computedStyle.top || "0");
          const stickyTop = Number.isNaN(topValue) ? 0 : topValue;
          if (rect.top <= stickyTop + EPSILON) return Math.max(max, rect.bottom);
        }
        return max;
      }, 0);
    const pinnedHeadersStack = Math.max(adminBarHeight, pinnedHeadersMaxBottom);

    // Calcola l'altezza massima del widget.
    const distance = Math.max(adminBarHeight + yOffsetWithinBody, pinnedHeadersStack + MIN_TOP_GAP_PX);
    const maxHeight = Math.max(vh - distance, 300);

    // Posizionamento orizzontale in base a left/right richiesto dalla config.
    if (position.includes("right")) {
      el.style.right = `${rightOffset}px`;
      el.style.left = "";
    } else {
      el.style.left = `${leftOffset}px`;
      el.style.right = "";
    }
    el.style.maxWidth = `${maxWidth}px`;

    // Posizionamento verticale e dimensionamento in altezza.
    el.style.setProperty("--aui-fixed-input-max-height", `${maxHeight}px`);
    el.style.setProperty("--aui-fixed-input-vh", `${vh}px`);
  };

  // Applica i vincoli all'inizializzazione.
  applyConstraints();

  if (xContainer || yContainer) {
    // Osserva i cambiamenti di dimensione della viewport per riapplicare i vincoli.
    if (window.visualViewport) {
      // Uso VisualViewport API se disponibile.
      window.visualViewport.addEventListener("resize", applyConstraints);
    } else if (typeof ResizeObserver !== "undefined") {
      // Uso ResizeObserver come fallback.
      const ro = new ResizeObserver(applyConstraints);
      ro.observe(document.body);
    } else {
      // Uso l'evento resize come ultima spiaggia.
      window.addEventListener("resize", applyConstraints);
    }

    // Osserva i cambiamenti di stile dell'header D360 (se presente) per evitare sovrapposizioni.
    const d360Header = document.querySelector(".header-d360");
    if (d360Header && typeof MutationObserver !== "undefined") {
      const mo = new MutationObserver(applyConstraints);
      mo.observe(d360Header, { attributes: true, attributeFilter: ["style", "class"] });
    }
  }

  document.body.appendChild(el);
  return el;
}

/**
 * Monta il widget nel container specificato.
 *
 * Workflow:
 * 1. Verifica supporto Shadow DOM
 * 2. Se supportato: crea Shadow DOM, inietta CSS adattato, monta React
 * 3. Se non supportato: mount diretto nel document (fallback)
 *
 * @param mount - Elemento HTML in cui montare il widget
 * @param config - Configurazione del widget
 * @param data - Dati runtime opzionali
 *
 * @remarks
 * Il Shadow DOM fornisce isolamento completo degli stili, prevenendo
 * interferenze con il CSS del sito host. Il fallback è per browser molto vecchi.
 * Per la modalità `standalone-cms`, il Shadow DOM viene disabilitato per permettere
 * l'accesso agli stili del documento principale (es. admin bar WordPress).
 */
async function renderWidget(mount: HTMLElement, config: WidgetConfig, data?: WidgetData): Promise<void> {
  const supportsShadowDOM = "attachShadow" in HTMLElement.prototype;
  const useDirectMount = !supportsShadowDOM || IS_DEV || config.mode === "standalone-cms";

  if (useDirectMount) {
    return renderWidgetDirect(mount, config, data);
  }

  // Crea Shadow Root.
  const shadowRoot = mount.attachShadow({ mode: "open" });

  // Inietta CSS (base + custom).
  await injectCss("chat-ui.css", config.customCss, shadowRoot);

  // Applica theme vars custom (se forniti).
  if (config.themeVars && Object.keys(config.themeVars).length > 0) {
    applyThemeVars(config.themeVars, shadowRoot);
  }

  // Prepara struttura DOM e monta React.
  const { portalContainer, reactMount } = prepareShadowDOMStructure(shadowRoot);
  await mountReactApp(reactMount, config, data, shadowRoot, portalContainer);
}

/**
 * Monta il widget direttamente nel document (fallback senza Shadow DOM).
 *
 * Utilizzato solo per browser che non supportano Shadow DOM o in dev mode.
 * Gli stili vengono iniettati nel `document.head` e potrebbero
 * interferire con il CSS del sito host.
 *
 * @param mount - Elemento HTML in cui montare il widget
 * @param config - Configurazione del widget
 * @param data - Dati runtime opzionali
 */
async function renderWidgetDirect(mount: HTMLElement, config: WidgetConfig, data?: WidgetData): Promise<void> {
  // Inietta CSS nel document.head.
  await injectCss("chat-ui.css", config.customCss, document.head);

  // Applica theme vars custom (se forniti).
  if (config.themeVars && Object.keys(config.themeVars).length > 0) {
    applyThemeVars(config.themeVars, null);
  }

  // Monta React direttamente.
  await mountReactApp(mount, config, data, null, null);
}

export {};
